import React, { useCallback, useState, useContext } from 'react';
import { Upload, File, X, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';

const FileUploadZone = ({ onFileSelect, onUpload, uploading, uploadError, selectedLibrary, disabled = false }) => {
  const { user } = useContext(AuthContext);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, [disabled]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [disabled]);

  const handleFileSelect = (file) => {
    // Validate file type
    const allowedTypes = [
      'application/json',
      'text/csv', 
      'application/csv',
      'text/xml',
      'application/xml',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    const allowedExtensions = ['.json', '.csv', '.xml', '.xls', '.xlsx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      alert('Please select a valid file type: JSON, CSV, XML, or Excel');
      return;
    }

    setSelectedFile(file);
    onFileSelect(file);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    onFileSelect(null);
  };

  const handleUpload = () => {
    if (selectedFile && selectedLibrary) {
      onUpload(selectedFile);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-[13px] leading-relaxed text-gray-600 dark:text-gray-300">
        <p className="mb-1">
          <strong className="font-medium text-gray-900 dark:text-gray-50">Supported formats:</strong> CDISC ODM XML (regulatory-compliant) or CSV
        </p>
        <p className="text-[12.5px] text-gray-500 dark:text-gray-400">
          Upload CDISC-compliant assessment files. The system will automatically detect the file format and ODM version.
        </p>
      </div>

      {/* File Drop Zone */}
      <div
        className={`
          relative rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors duration-150
          ${dragActive
            ? 'border-primary-500 bg-primary-50/40 dark:border-primary-400 dark:bg-primary-400/10'
            : selectedFile
              ? 'border-gray-300 bg-white dark:border-white/15 dark:bg-white/[0.02]'
              : 'border-gray-300 bg-gray-50/60 hover:border-gray-400 dark:border-white/15 dark:bg-white/[0.02] dark:hover:border-white/25'
          }
          ${uploading || disabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !uploading && !disabled && document.getElementById('file-input').click()}
      >
        <input
          id="file-input"
          type="file"
          className="hidden"
          accept=".json,.csv,.xml,.xls,.xlsx"
          onChange={handleFileInputChange}
          disabled={uploading || disabled}
        />

        {!selectedFile ? (
          <div className="space-y-3">
            <Upload className={`mx-auto h-6 w-6 ${dragActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                {dragActive ? 'Drop your file here' : 'Upload CDISC Assessment File'}
              </p>
              <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
                Drag and drop your file here, or <span className="font-medium text-primary-700 dark:text-primary-400">browse</span>
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-3">
            <File className="h-5 w-5 flex-shrink-0 text-primary-600 dark:text-primary-400" />
            <div className="min-w-0 text-left">
              <p className="truncate text-[13.5px] font-medium text-gray-900 dark:text-gray-50">{selectedFile.name}</p>
              <p className="text-[12.5px] tabular-nums text-gray-500 dark:text-gray-400">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
            {!uploading && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile();
                }}
                className="btn btn-ghost btn-sm btn-icon text-gray-400 hover:text-red-600 dark:hover:text-red-400"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/80 dark:bg-gray-900/80">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
              <span className="text-[13px] font-medium text-gray-700 dark:text-gray-200">Uploading and processing...</span>
            </div>
          </div>
        )}
      </div>

      {/* Upload Error */}
      {uploadError && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3.5 dark:border-red-500/30 dark:bg-red-500/10">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400" />
          <div>
            <h4 className="text-[13px] font-medium text-red-800 dark:text-red-200">Upload Failed</h4>
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-red-700 dark:text-red-300">{uploadError}</p>
          </div>
        </div>
      )}

      {/* Library Warning */}
      {!selectedLibrary && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3.5 dark:border-amber-500/30 dark:bg-amber-500/10">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-300" />
          <div>
            <h4 className="text-[13px] font-medium text-amber-800 dark:text-amber-200">Select Library First</h4>
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-amber-800 dark:text-amber-200">
              Please choose a library location above before uploading your file.
            </p>
          </div>
        </div>
      )}

      {/* Upload Button */}
      {selectedFile && selectedLibrary && !uploading && (
        <div className="flex justify-end">
          <button
            onClick={handleUpload}
            disabled={disabled}
            className="btn btn-primary"
          >
            <Upload className="h-4 w-4" />
            Upload & Process Assessment
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;
